/**
 * Serviço para gerenciar notificações de novas features
 */

interface FeatureNotification {
  name: string;
  description: string;
  icon?: string;
  migrationId: string;
}

/**
 * Envia notificação local sobre nova feature
 */
export async function notifyNewFeature(feature: FeatureNotification): Promise<void> {
  // Verificar se notificações são suportadas
  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    console.log('⚠️ Notificações não suportadas neste navegador');
    return;
  }

  // Verificar permissão
  if (Notification.permission !== 'granted') {
    console.log('⚠️ Permissão de notificação não concedida');
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    
    await registration.showNotification('🎉 Nova Feature Disponível!', {
      body: `${feature.icon || '✨'} ${feature.name}\n\n${feature.description}`,
      icon: '/logo/logo-icon.png',
      badge: '/logo/logo-icon.png',
      tag: `feature-${feature.migrationId}`,
      requireInteraction: true,
      data: {
        type: 'feature',
        migrationId: feature.migrationId,
        url: window.location.origin
      }
    } as any); // Type assertion necessária para incluir actions em alguns browsers

    console.log(`✅ Notificação de feature enviada: ${feature.name}`);
  } catch (error) {
    console.error('❌ Erro ao enviar notificação de feature:', error);
  }
}

/**
 * Solicita permissão para notificações (se ainda não foi concedida)
 */
export async function requestNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) {
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission === 'denied') {
    return false;
  }

  try {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  } catch (error) {
    console.error('❌ Erro ao solicitar permissão de notificação:', error);
    return false;
  }
}

/**
 * Verifica se há permissão para notificações
 */
export function hasNotificationPermission(): boolean {
  return 'Notification' in window && Notification.permission === 'granted';
}

/**
 * Envia notificação de boas-vindas ao sistema de features
 */
export async function sendWelcomeNotification(): Promise<void> {
  if (!hasNotificationPermission()) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    
    await registration.showNotification('👋 Bem-vindo ao sistema de notificações!', {
      body: 'Você receberá avisos quando novas features forem liberadas após atualizações do app.',
      icon: '/logo/logo-icon.png',
      badge: '/logo/logo-icon.png',
      tag: 'welcome-notification',
      data: {
        type: 'welcome',
        url: window.location.origin
      }
    });

    console.log('✅ Notificação de boas-vindas enviada');
  } catch (error) {
    console.error('❌ Erro ao enviar notificação de boas-vindas:', error);
  }
}

/**
 * Envia notificação de teste
 */
export async function sendTestNotification(): Promise<void> {
  if (!hasNotificationPermission()) {
    const granted = await requestNotificationPermission();
    if (!granted) {
      console.log('⚠️ Permissão de notificação negada');
      return;
    }
  }

  await notifyNewFeature({
    name: 'Notificação de Teste',
    description: 'Se você está vendo isso, as notificações estão funcionando perfeitamente! 🎉',
    icon: '🧪',
    migrationId: 'test'
  });
}
